/* The remaining blotter tabs: executions, time & sales, market depth and
 * strategies. Each shows real structure rather than a placeholder, and each
 * says plainly when it has no data instead of inventing some.
 */
import { useMemo } from 'react'
import { useStore } from '@/store'
import { fmtMoney, fmtPrice, fmtQty, fmtTime } from '@/lib/format'

const th: React.CSSProperties = {
  padding: 'var(--sp-1) var(--sp-3)', textAlign: 'left',
  fontSize: 'var(--fs-2xs)', fontWeight: 600, letterSpacing: '0.05em',
  textTransform: 'uppercase', color: 'var(--text-muted)',
  borderBottom: '1px solid var(--border)',
  position: 'sticky', top: 0, background: 'var(--bg-panel)', zIndex: 1,
  whiteSpace: 'nowrap',
}

const td: React.CSSProperties = {
  padding: '0 var(--sp-3)', height: 'var(--row-h)',
  fontSize: 'var(--fs-sm)', borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
}

export function Empty({ message }: { message: string }) {
  return (
    <div style={{
      display: 'grid', placeItems: 'center', height: '100%',
      color: 'var(--text-muted)', fontSize: 'var(--fs-sm)',
      textAlign: 'center', padding: 'var(--sp-4)', lineHeight: 1.6,
    }}>
      {message}
    </div>
  )
}

/* ---------------------------------------------------------------- executions */
export function ExecutionsTable() {
  const executions = useStore((s) => s.executions)

  const totals = useMemo(() => {
    let value = 0, costs = 0
    for (const e of executions) {
      value += Number(e.gross_value) || 0
      costs += Number(e.total_costs) || 0
    }
    return { value, costs }
  }, [executions])

  if (executions.length === 0)
    return <Empty message="No executions in this session." />

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Time</th>
          <th style={th}>Symbol</th>
          <th style={th}>Side</th>
          <th style={{ ...th, textAlign: 'right' }}>Qty</th>
          <th style={{ ...th, textAlign: 'right' }}>Price</th>
          <th style={{ ...th, textAlign: 'right' }}>Value</th>
          <th style={{ ...th, textAlign: 'right' }}>Costs</th>
          <th style={{ ...th, textAlign: 'right' }}>Net</th>
          <th style={th}>Mode</th>
        </tr>
      </thead>
      <tbody>
        {executions.map((e) => (
          <tr key={e.id}>
            <td className="num" style={{ ...td, color: 'var(--text-secondary)' }}>
              {fmtTime(e.exec_time)}
            </td>
            <td style={{ ...td, fontWeight: 600 }}>{e.symbol ?? e.instrument_id}</td>
            <td style={{
              ...td, fontWeight: 700,
              color: e.side === 'BUY' ? 'var(--buy)' : 'var(--sell)',
            }}>
              {e.side}
            </td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>{fmtQty(e.quantity)}</td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>{fmtPrice(e.price)}</td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>
              {fmtMoney(e.gross_value, e.currency, true)}
            </td>
            {/* Costs are shown per execution, not buried in a summary: they are
                the difference between a viable strategy and an unviable one. */}
            <td className="num" style={{ ...td, textAlign: 'right', color: 'var(--warn)' }}>
              {fmtMoney(e.total_costs, e.currency, true)}
            </td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>
              {fmtMoney(e.net_value, e.currency, true)}
            </td>
            <td style={{ ...td, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
              {e.trading_mode}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr style={{ background: 'var(--bg-raised)', fontWeight: 600 }}>
          <td style={{ ...td, borderBottom: 'none' }} colSpan={5}>
            {executions.length} executions
          </td>
          <td className="num" style={{ ...td, textAlign: 'right', borderBottom: 'none' }}>
            {fmtMoney(totals.value, 'INR', true)}
          </td>
          <td className="num" style={{
            ...td, textAlign: 'right', borderBottom: 'none', color: 'var(--warn)',
          }}>
            {fmtMoney(totals.costs, 'INR', true)}
          </td>
          <td style={{ ...td, borderBottom: 'none' }} colSpan={2} />
        </tr>
      </tfoot>
    </table>
  )
}

/* -------------------------------------------------------------- time & sales */
export function TimeAndSales() {
  const instrument = useStore((s) => s.selectedInstrument)
  const prints = useStore((s) => s.timeAndSales)

  if (!instrument)
    return <Empty message="Select an instrument to see its trade prints." />
  if (prints.length === 0)
    return <Empty message="No trade prints received for this instrument yet." />

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Time</th>
          <th style={{ ...th, textAlign: 'right' }}>Price</th>
          <th style={{ ...th, textAlign: 'right' }}>Size</th>
          <th style={th}>Aggressor</th>
        </tr>
      </thead>
      <tbody>
        {prints.slice(0, 200).map((p, i) => (
          <tr key={`${p.ts}-${i}`}>
            <td className="num" style={{ ...td, color: 'var(--text-secondary)' }}>
              {fmtTime(p.ts)}
            </td>
            <td className="num" style={{
              ...td, textAlign: 'right',
              // Colour by which side lifted: a buyer taking the offer is
              // different information from a seller hitting the bid.
              color: p.aggressor === 'BUY' ? 'var(--buy)' : 'var(--sell)',
            }}>
              {fmtPrice(p.price)}
            </td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>{fmtQty(p.size)}</td>
            <td style={{ ...td, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
              {p.aggressor === 'BUY' ? 'buyer lifted' : 'seller hit'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/* --------------------------------------------------------------- market depth */
export function MarketDepth() {
  const instrument = useStore((s) => s.selectedInstrument)
  const quote = useStore((s) => (instrument ? s.quotes.get(instrument) : undefined))
  const depth = useStore((s) => (instrument ? s.depth.get(instrument) : undefined))

  if (!instrument) return <Empty message="Select an instrument to see its depth." />

  if (quote?.stale)
    return (
      <Empty message="Market data for this instrument is STALE. The book shown would not reflect tradable prices, so it is withheld." />
    )

  // Level 1 is always available from the quote; deeper levels arrive only when
  // the venue publishes them.
  const bids = depth?.bids ?? (quote ? [{ price: quote.bid, qty: quote.bidQty ?? '0' }] : [])
  const asks = depth?.asks ?? (quote ? [{ price: quote.ask, qty: quote.askQty ?? '0' }] : [])

  if (bids.length === 0 && asks.length === 0)
    return <Empty message="No depth received for this instrument yet." />

  const maxQty = Math.max(
    ...bids.map((b) => Number(b.qty) || 0),
    ...asks.map((a) => Number(a.qty) || 0),
    1,
  )
  const rows = Math.max(bids.length, asks.length)

  return (
    <div style={{ padding: 'var(--sp-2)' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)',
      }}>
        <DepthSide side="bid" levels={bids} maxQty={maxQty} rows={rows} />
        <DepthSide side="ask" levels={asks} maxQty={maxQty} rows={rows} />
      </div>
      {quote && (
        <div style={{
          marginTop: 'var(--sp-3)', paddingTop: 'var(--sp-2)',
          borderTop: '1px solid var(--border)',
          display: 'flex', gap: 'var(--sp-5)', justifyContent: 'center',
          fontSize: 'var(--fs-xs)', color: 'var(--text-secondary)',
        }}>
          <span>Spread <span className="num" style={{ color: 'var(--text-primary)' }}>
            {fmtPrice(Number(quote.ask) - Number(quote.bid))}
          </span></span>
          <span>Mid <span className="num" style={{ color: 'var(--text-primary)' }}>
            {fmtPrice((Number(quote.bid) + Number(quote.ask)) / 2)}
          </span></span>
        </div>
      )}
    </div>
  )
}

function DepthSide({
  side, levels, maxQty, rows,
}: {
  side: 'bid' | 'ask'
  levels: { price: string; qty: string }[]
  maxQty: number
  rows: number
}) {
  const color = side === 'bid' ? 'var(--buy)' : 'var(--sell)'
  const bg = side === 'bid' ? 'var(--buy-bg)' : 'var(--sell-bg)'

  return (
    <div>
      <div style={{
        display: 'flex', justifyContent: 'space-between',
        fontSize: 'var(--fs-2xs)', textTransform: 'uppercase',
        letterSpacing: '0.06em', color: 'var(--text-muted)',
        paddingBottom: 'var(--sp-1)', borderBottom: '1px solid var(--border)',
      }}>
        <span>{side === 'bid' ? 'Bid' : 'Ask'}</span>
        <span>Size</span>
      </div>
      {Array.from({ length: rows }).map((_, i) => {
        const lvl = levels[i]
        if (!lvl) return <div key={i} style={{ height: 'var(--row-h)' }} />
        const pct = ((Number(lvl.qty) || 0) / maxQty) * 100
        return (
          <div key={i} style={{
            position: 'relative', display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', height: 'var(--row-h)',
            padding: '0 var(--sp-2)', fontSize: 'var(--fs-sm)',
          }}>
            {/* A size bar makes relative depth readable at a glance; the number
                alone requires arithmetic the eye should not have to do. */}
            <div style={{
              position: 'absolute',
              [side === 'bid' ? 'right' : 'left']: 0,
              top: 2, bottom: 2, width: `${pct}%`,
              background: bg, borderRadius: 2,
            }} />
            <span className="num" style={{ color, zIndex: 1 }}>{fmtPrice(lvl.price)}</span>
            <span className="num" style={{ zIndex: 1, color: 'var(--text-secondary)' }}>
              {fmtQty(lvl.qty)}
            </span>
          </div>
        )
      })}
    </div>
  )
}

/* ---------------------------------------------------------------- strategies */
export function StrategiesTable() {
  const strategies = useStore((s) => s.strategies)

  if (strategies.length === 0)
    return <Empty message="No strategies are loaded in the engine." />

  const stateColor = (state: string) => {
    if (state === 'RUNNING') return 'var(--ok)'
    if (state === 'QUARANTINED' || state === 'FAILED') return 'var(--danger)'
    if (state === 'PAUSED') return 'var(--warn)'
    return 'var(--text-muted)'
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Strategy</th>
          <th style={th}>State</th>
          <th style={th}>Mode</th>
          <th style={{ ...th, textAlign: 'right' }}>Orders</th>
          <th style={{ ...th, textAlign: 'right' }}>Rejected</th>
          <th style={{ ...th, textAlign: 'right' }}>Bars</th>
          <th style={{ ...th, textAlign: 'right' }}>Faults</th>
          <th style={th}>Note</th>
        </tr>
      </thead>
      <tbody>
        {strategies.map((s) => (
          <tr key={s.id}>
            <td style={{ ...td, fontWeight: 600 }}>{s.name}</td>
            <td style={{ ...td, color: stateColor(s.state), fontWeight: 600 }}>
              {s.state}
            </td>
            <td style={{ ...td, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)' }}>
              {s.mode}
            </td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>{s.orders_submitted}</td>
            <td className="num" style={{
              ...td, textAlign: 'right',
              color: s.orders_rejected > 0 ? 'var(--warn)' : undefined,
            }}>
              {s.orders_rejected}
            </td>
            <td className="num" style={{ ...td, textAlign: 'right' }}>{s.bars}</td>
            <td className="num" style={{
              ...td, textAlign: 'right',
              color: s.exceptions > 0 ? 'var(--danger)' : undefined,
            }}>
              {s.exceptions}
            </td>
            <td style={{
              ...td, fontSize: 'var(--fs-2xs)', color: 'var(--text-muted)',
              maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {s.fault || '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
